import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search as SearchIcon } from 'lucide-react';
import { barsApi } from '@/api/endpoints/bars';
import { BarCard } from '@/components/domain';
import { PageSpinner, Card, CardContent, Input } from '@/components/ui';
import { useFilters } from '@/contexts/FilterContext';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import { useDebounce } from '@/hooks/useDebounce';

export function SearchPage() {
  const { lat, lng, radius, hasValidLocation } = useFilters();
  const { hidden } = useUserPreferences();
  const [searchQuery, setSearchQuery] = useState('');

  // Debounce search query (300ms) to avoid excessive API calls
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Fetch bars with combined search + location
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ['search-bars', { debouncedSearch, lat, lng, radius }],
    queryFn: () =>
      barsApi.list({
        lat: lat!,
        lng: lng!,
        radius,
        search: debouncedSearch || undefined,
        limit: 50,
      }),
    // Only fetch when there's a search term and valid location
    enabled: hasValidLocation && debouncedSearch.trim().length > 0,
    placeholderData: (previousData) => previousData,
  });

  // Filter out hidden bars
  const filteredBars = useMemo(() => {
    const bars = data?.bars ?? [];
    return bars.filter((bar) => !hidden.has(bar.id));
  }, [data?.bars, hidden]);

  const showResults = debouncedSearch.trim().length > 0;
  const hasQuery = searchQuery.trim().length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Search Bars</h1>
        <p className="mt-1 text-[var(--color-muted)]">
          Find bars by name, sorted by distance
        </p>
      </div>

      {/* Search input */}
      <div className="relative">
        <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted)]" />
        <Input
          type="search"
          placeholder="Search bars by name or address..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 pr-9"
          autoFocus
        />
        {isFetching && hasQuery && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-accent-primary border-t-transparent" />
          </div>
        )}
      </div>

      {/* Results */}
      {!showResults ? (
        <Card>
          <CardContent className="flex flex-col items-center py-12 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-primary/10">
              <SearchIcon className="h-8 w-8 text-accent-primary" />
            </div>
            <h2 className="mt-4 text-lg font-semibold">Start searching</h2>
            <p className="mt-2 max-w-sm text-sm text-[var(--color-muted)]">
              Type a bar name or address to find nearby bars.
            </p>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <PageSpinner />
      ) : isError ? (
        <div className="py-12 text-center">
          <p className="text-[var(--color-muted)]">Failed to search bars</p>
        </div>
      ) : filteredBars.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-8 text-center">
            <p className="text-[var(--color-muted)]">
              No bars found matching "{debouncedSearch}"
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredBars.map((bar) => (
            <BarCard key={bar.id} bar={bar} />
          ))}
        </div>
      )}
    </div>
  );
}
